// Per-weapon settings store + resolver.
//
// Resolution chain: global settings -> `defaults` profile -> weapon override.
// Every feel-affecting client value is read through `resolveWeapon` so the HUD,
// the renderer and the input path cannot disagree.

import { clamp, isHex } from "../crosshair/types";

export const WEAPON_SETTINGS_VERSION = 2;

/** Weapons that can zoom (Warfork): machinegun 2, lightning 7, railgun 8. */
export const ZOOM_WEAPONS = new Set<number>([2, 7, 8]);
/** Weapons with no weak fire mode: their alternate fire is always zoom. */
export const ALT_ZOOM_ONLY = new Set<number>([7, 8]);

export type WeaponPositionPreset = "hidden" | "right" | "center" | "left";
export type Mouse4Action = "secondary" | "zoom";
export type ZoomSensMode = "linked" | "absolute";
export type AccelType = "off" | "linear" | "power" | "classic";
/** How a projectile leaves its trail (client VFX). */
export type TrailMode = "both" | "glow" | "smoke" | "off";

export interface WeaponPositionConfig {
  preset: WeaponPositionPreset;
  x: number;
  y: number;
  z: number;
  size: number;
}

/**
 * Placeholder for the acceleration session: stored and validated now, not
 * applied yet. The real curve gets its own focused session.
 */
export interface AccelConfig {
  type: AccelType;
  scale: number;
  exponent: number;
  cap: number;
}

export interface WeaponProfile {
  sensitivity?: number;
  zoomSensitivity?: number;
  zoomSensMode?: ZoomSensMode;
  yawMultiplier?: number;
  pitchMultiplier?: number;
  accel?: AccelConfig;
  fov?: number;
  zoomFov?: number;
  zoomToggle?: boolean;
  zoomTime?: number;
  zoomEnabled?: boolean;
  mouse4?: Mouse4Action;
  crosshair?: string;
  zoomCrosshair?: string;
  hitSounds?: boolean;
  weaponPosition?: WeaponPositionConfig;
  /** Per-weapon client VFX (smoke / particles / projectile trail). */
  vfxSmoke?: boolean;
  vfxParticles?: boolean;
  vfxTrailMode?: TrailMode;
  vfxSmokeAmount?: number;
  vfxSmokeOpacity?: number;
  vfxSmokeSize?: number;
  vfxParticleAmount?: number;
  vfxParticleSize?: number;
  /** Hitscan beam linger time in ms (rail = 400, others ~100). */
  vfxBeamTrailMs?: number;
  /** How long the bright glow puffs behind a projectile live (ms). */
  vfxTrailMs?: number;
  /** How long the thin smoke wisps behind a projectile live (ms). */
  vfxSmokeTrailMs?: number;
}

export interface WeaponSettings {
  version: 2;
  enabled: boolean;
  defaults: WeaponProfile;
  overrides: Record<string, WeaponProfile>;
}

export interface GlobalDefaults {
  sensitivity: number;
  fov: number;
  hitSounds: boolean;
  crosshair: string;
}

export interface ResolvedWeapon {
  sensitivity: number;
  zoomSensitivity: number;
  zoomSensMode: ZoomSensMode;
  yawMultiplier: number;
  pitchMultiplier: number;
  accel: AccelConfig;
  fov: number;
  zoomFov: number;
  zoomToggle: boolean;
  zoomTime: number;
  zoomEnabled: boolean;
  mouse4: Mouse4Action;
  crosshair: string;
  zoomCrosshair: string;
  hitSounds: boolean;
  weaponPosition: WeaponPositionConfig;
  vfxSmoke: boolean;
  vfxParticles: boolean;
  vfxTrailMode: TrailMode;
  vfxSmokeAmount: number;
  vfxSmokeOpacity: number;
  vfxSmokeSize: number;
  vfxParticleAmount: number;
  vfxParticleSize: number;
  vfxBeamTrailMs: number;
  vfxTrailMs: number;
  vfxSmokeTrailMs: number;
}

const POSITION_PRESETS: WeaponPositionPreset[] = ["hidden", "right", "center", "left"];
const MOUSE4: Mouse4Action[] = ["secondary", "zoom"];
const ZOOM_MODES: ZoomSensMode[] = ["linked", "absolute"];
const ACCEL_TYPES: AccelType[] = ["off", "linear", "power", "classic"];

export const DEFAULT_ACCEL: AccelConfig = { type: "off", scale: 1, exponent: 1, cap: 100000 };
export const DEFAULT_POSITION: WeaponPositionConfig = {
  preset: "right",
  x: -7,
  y: -4,
  z: 2,
  size: 0.85,
};

/** Baseline per-weapon VFX (the "good out of the box" look). */
const DEFAULT_VFX = {
  vfxSmoke: true,
  vfxParticles: true,
  vfxTrailMode: "both" as TrailMode,
  vfxSmokeAmount: 1,
  vfxSmokeOpacity: 1,
  vfxSmokeSize: 1,
  vfxParticleAmount: 1,
  vfxParticleSize: 1,
  vfxBeamTrailMs: 100,
  vfxTrailMs: 320,
  vfxSmokeTrailMs: 550,
};

/**
 * Shipped per-weapon look. Rockets/grenades lean smoke, plasma leans glow.
 * Every field can be overridden in Settings -> Weapons -> <weapon> -> Effects.
 */
const WEAPON_VFX: Record<number, Partial<typeof DEFAULT_VFX>> = {
  1: { vfxSmokeAmount: 0, vfxParticleAmount: 0.8, vfxTrailMode: "off" }, // melee
  2: { vfxSmokeAmount: 0.4, vfxParticleAmount: 0.8, vfxTrailMode: "glow" }, // MG
  3: { vfxSmokeAmount: 0.5, vfxParticleAmount: 0.9, vfxTrailMode: "glow" }, // shotgun
  4: { vfxSmokeAmount: 1.6, vfxParticleAmount: 0.5, vfxSmokeSize: 1.2, vfxTrailMode: "smoke" }, // grenade
  5: { vfxSmokeAmount: 1.5, vfxParticleAmount: 0.7, vfxSmokeSize: 1.15, vfxTrailMode: "smoke" }, // rocket
  6: { vfxSmokeAmount: 0.25, vfxParticleAmount: 1.3, vfxTrailMode: "glow" }, // plasma
  7: { vfxSmokeAmount: 0.1, vfxParticleAmount: 1.0, vfxTrailMode: "glow" }, // lightning
  8: { vfxSmoke: false, vfxParticleAmount: 1.2, vfxTrailMode: "glow", vfxBeamTrailMs: 400 }, // rail
  9: { vfxSmokeAmount: 0.8, vfxParticleAmount: 1.0, vfxTrailMode: "glow" }, // experimental
  10: { vfxSmokeAmount: 0.4, vfxParticleAmount: 1.0, vfxTrailMode: "glow" }, // ball
};
const TRAIL_MODES: TrailMode[] = ["both", "glow", "smoke", "off"];

export const DEFAULT_WEAPON_SETTINGS: WeaponSettings = {
  version: 2,
  enabled: true,
  // Yaw/pitch multipliers are explicit defaults so they are real registry
  // settings (searchable / resettable), rather than only fallbacks in code.
  defaults: { yawMultiplier: 1, pitchMultiplier: 1 },
  overrides: {},
};

/** Fresh, independent copy of the default store. */
export function defaultWeaponSettings(): WeaponSettings {
  return normalizeWeaponSettings(DEFAULT_WEAPON_SETTINGS);
}

const num = (v: unknown, d: number, min: number, max: number): number =>
  typeof v === "number" && Number.isFinite(v) ? clamp(v, min, max) : d;
const bool = (v: unknown, d: boolean): boolean => (typeof v === "boolean" ? v : d);
const text = (v: unknown, d: string, max = 32): string =>
  typeof v === "string" ? v.replace(/[\u0000-\u001f]/g, "").slice(0, max) : d;

function normAccel(raw: unknown): AccelConfig | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  return {
    type: ACCEL_TYPES.includes(o.type as AccelType) ? (o.type as AccelType) : "off",
    scale: num(o.scale, 1, 0, 10),
    exponent: num(o.exponent, 1, 0.1, 10),
    cap: num(o.cap, 100000, 1, 1e6),
  };
}

function normPosition(raw: unknown): WeaponPositionConfig | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  return {
    preset: POSITION_PRESETS.includes(o.preset as WeaponPositionPreset)
      ? (o.preset as WeaponPositionPreset)
      : DEFAULT_POSITION.preset,
    x: num(o.x, DEFAULT_POSITION.x, -100, 100),
    y: num(o.y, DEFAULT_POSITION.y, -100, 100),
    z: num(o.z, DEFAULT_POSITION.z, -100, 100),
    size: num(o.size, DEFAULT_POSITION.size, 0.2, 3),
  };
}

/** Validate a partial profile; unknown fields are dropped. */
export function normalizeProfile(raw: unknown): WeaponProfile {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const out: WeaponProfile = {};
  if (typeof o.sensitivity === "number") out.sensitivity = num(o.sensitivity, 2, 0.05, 50);
  if (typeof o.zoomSensitivity === "number") out.zoomSensitivity = num(o.zoomSensitivity, 0.4, 0.01, 50);
  if (o.zoomSensMode !== undefined && ZOOM_MODES.includes(o.zoomSensMode as ZoomSensMode)) {
    out.zoomSensMode = o.zoomSensMode as ZoomSensMode;
  }
  if (typeof o.yawMultiplier === "number") out.yawMultiplier = num(o.yawMultiplier, 1, 0.05, 10);
  if (typeof o.pitchMultiplier === "number") out.pitchMultiplier = num(o.pitchMultiplier, 1, 0.05, 10);
  if (o.accel !== undefined) out.accel = normAccel(o.accel);
  if (typeof o.fov === "number") out.fov = num(o.fov, 125, 60, 160);
  if (typeof o.zoomFov === "number") out.zoomFov = num(o.zoomFov, 42, 10, 160);
  if (typeof o.zoomToggle === "boolean") out.zoomToggle = o.zoomToggle;
  if (typeof o.zoomTime === "number") out.zoomTime = num(o.zoomTime, 0, 0, 500);
  if (typeof o.zoomEnabled === "boolean") out.zoomEnabled = o.zoomEnabled;
  if (o.mouse4 !== undefined && MOUSE4.includes(o.mouse4 as Mouse4Action)) {
    out.mouse4 = o.mouse4 as Mouse4Action;
  }
  if (typeof o.crosshair === "string") out.crosshair = text(o.crosshair, "", 24);
  if (typeof o.zoomCrosshair === "string") out.zoomCrosshair = text(o.zoomCrosshair, "", 24);
  if (typeof o.hitSounds === "boolean") out.hitSounds = o.hitSounds;
  if (o.weaponPosition !== undefined) out.weaponPosition = normPosition(o.weaponPosition);
  if (typeof o.vfxSmoke === "boolean") out.vfxSmoke = o.vfxSmoke;
  if (typeof o.vfxParticles === "boolean") out.vfxParticles = o.vfxParticles;
  if (o.vfxTrailMode !== undefined && TRAIL_MODES.includes(o.vfxTrailMode as TrailMode)) {
    out.vfxTrailMode = o.vfxTrailMode as TrailMode;
  }
  if (typeof o.vfxSmokeAmount === "number") out.vfxSmokeAmount = num(o.vfxSmokeAmount, 1, 0, 3);
  if (typeof o.vfxSmokeOpacity === "number") out.vfxSmokeOpacity = num(o.vfxSmokeOpacity, 1, 0, 1);
  if (typeof o.vfxSmokeSize === "number") out.vfxSmokeSize = num(o.vfxSmokeSize, 1, 0.2, 3);
  if (typeof o.vfxParticleAmount === "number") {
    out.vfxParticleAmount = num(o.vfxParticleAmount, 1, 0, 3);
  }
  if (typeof o.vfxParticleSize === "number") {
    out.vfxParticleSize = num(o.vfxParticleSize, 1, 0.2, 3);
  }
  if (typeof o.vfxBeamTrailMs === "number") {
    out.vfxBeamTrailMs = num(o.vfxBeamTrailMs, 100, 0, 2000);
  }
  if (typeof o.vfxTrailMs === "number") {
    out.vfxTrailMs = num(o.vfxTrailMs, 320, 40, 2000);
  }
  if (typeof o.vfxSmokeTrailMs === "number") {
    out.vfxSmokeTrailMs = num(o.vfxSmokeTrailMs, 550, 40, 3000);
  }
  return out;
}

export function normalizeWeaponSettings(raw: unknown): WeaponSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const overrides: Record<string, WeaponProfile> = {};
  if (o.overrides && typeof o.overrides === "object") {
    for (const [k, v] of Object.entries(o.overrides as Record<string, unknown>)) {
      const id = Number(k);
      if (!Number.isInteger(id) || id < 1 || id > 10) continue;
      const p = normalizeProfile(v);
      if (Object.keys(p).length) overrides[String(id)] = p;
    }
  }
  // The *base* sensitivity is owned by Settings → Controls (the sensitivity
  // converter + weapon tags). Drop the legacy shared-default sensitivity so a
  // value left over from the old Weapons tab can never override it.
  const defaults = normalizeProfile(o.defaults);
  delete defaults.sensitivity;
  return {
    version: WEAPON_SETTINGS_VERSION,
    enabled: bool(o.enabled, true),
    defaults,
    overrides,
  };
}

/**
 * v1 -> v2: the shipped viewmodel placement moved from dead-centre
 * (`0/0/0`, size 1) to the right-hand default (`-7/-4/2`, size 0.85). Move
 * stored positions that still hold the *exact* old default; a customised
 * position (any changed field, another preset, or "hidden") is left alone.
 */
export function migrateWeaponPositionsV2(ws: WeaponSettings): void {
  const old = { preset: "right", x: 0, y: 0, z: 0, size: 1 } as const;
  const fix = (p: WeaponProfile): void => {
    const wp = p.weaponPosition;
    if (
      !wp ||
      wp.preset !== old.preset ||
      wp.x !== old.x ||
      wp.y !== old.y ||
      wp.z !== old.z ||
      wp.size !== old.size
    ) {
      return;
    }
    p.weaponPosition = { ...DEFAULT_POSITION };
  };
  fix(ws.defaults);
  for (const p of Object.values(ws.overrides)) fix(p);
}

function mergeProfile(base: WeaponProfile, over: WeaponProfile | undefined): WeaponProfile {
  if (!over) return base;
  const out: WeaponProfile = { ...base, ...over };
  if (base.accel || over.accel) out.accel = { ...DEFAULT_ACCEL, ...base.accel, ...over.accel };
  if (base.weaponPosition || over.weaponPosition) {
    out.weaponPosition = { ...DEFAULT_POSITION, ...base.weaponPosition, ...over.weaponPosition };
  }
  return out;
}

/** The tangent-ratio zoom sensitivity Diabotical uses (same screen distance). */
export function linkedZoomSensitivity(sens: number, fov: number, zoomFov: number): number {
  const d = Math.tan((fov * Math.PI) / 360);
  if (d <= 1e-6) return sens;
  return clamp((sens * Math.tan((zoomFov * Math.PI) / 360)) / d, 0.01, 50);
}

export function resolveWeapon(
  ws: WeaponSettings,
  globals: GlobalDefaults,
  weapon: number | null,
): ResolvedWeapon {
  // The shared-defaults profile's `sensitivity` is legacy (the old Weapons tab
  // edited it): the base sensitivity is the Settings value, and only a
  // per-weapon override may replace it (the Controls weapon tags write those).
  // Ignore it even if an old stored profile still carries it.
  const weaponVfx = weapon !== null ? { ...DEFAULT_VFX, ...(WEAPON_VFX[weapon] ?? {}) } : { ...DEFAULT_VFX };
  const base: WeaponProfile = ws.enabled ? { ...weaponVfx, ...ws.defaults } : weaponVfx;
  delete base.sensitivity;
  const key = weapon !== null && Number.isInteger(weapon) ? String(weapon) : "";
  const prof = ws.enabled ? mergeProfile(base, key ? ws.overrides[key] : undefined) : base;
  const zoomCapable = ZOOM_WEAPONS.has(weapon ?? -1);
  // Rail/LG have no weak fire: alt-fire is always zoom, whatever the profile
  // says (including old saved overrides that pointed at "secondary").
  const altZoomOnly = ALT_ZOOM_ONLY.has(weapon ?? -1);

  const fov = clamp(prof.fov ?? globals.fov, 60, 160);
  const zoomFov = clamp(prof.zoomFov ?? Math.min(42, fov - 5), 10, 160);
  const mode = prof.zoomSensMode ?? "linked";
  const sensitivity = clamp(prof.sensitivity ?? globals.sensitivity, 0.05, 50);
  const zoomSensitivity =
    mode === "absolute" && prof.zoomSensitivity
      ? clamp(prof.zoomSensitivity, 0.01, 50)
      : linkedZoomSensitivity(sensitivity, fov, Math.min(zoomFov, fov - 1));

  return {
    sensitivity,
    zoomSensitivity,
    zoomSensMode: mode,
    yawMultiplier: prof.yawMultiplier ?? 1,
    pitchMultiplier: prof.pitchMultiplier ?? 1,
    accel: { ...DEFAULT_ACCEL, ...prof.accel },
    fov,
    zoomFov,
    zoomToggle: prof.zoomToggle ?? false,
    zoomTime: prof.zoomTime ?? 0,
    zoomEnabled: altZoomOnly ? true : (prof.zoomEnabled ?? zoomCapable),
    mouse4: altZoomOnly ? "zoom" : (prof.mouse4 ?? (zoomCapable ? "zoom" : "secondary")),
    crosshair: prof.crosshair || globals.crosshair,
    zoomCrosshair: prof.zoomCrosshair || "",
    hitSounds: prof.hitSounds ?? globals.hitSounds,
    weaponPosition: { ...DEFAULT_POSITION, ...prof.weaponPosition },
    vfxSmoke: prof.vfxSmoke ?? DEFAULT_VFX.vfxSmoke,
    vfxParticles: prof.vfxParticles ?? DEFAULT_VFX.vfxParticles,
    vfxTrailMode: prof.vfxTrailMode ?? DEFAULT_VFX.vfxTrailMode,
    vfxSmokeAmount: prof.vfxSmokeAmount ?? DEFAULT_VFX.vfxSmokeAmount,
    vfxSmokeOpacity: prof.vfxSmokeOpacity ?? DEFAULT_VFX.vfxSmokeOpacity,
    vfxSmokeSize: prof.vfxSmokeSize ?? DEFAULT_VFX.vfxSmokeSize,
    vfxParticleAmount: prof.vfxParticleAmount ?? DEFAULT_VFX.vfxParticleAmount,
    vfxParticleSize: prof.vfxParticleSize ?? DEFAULT_VFX.vfxParticleSize,
    vfxBeamTrailMs: prof.vfxBeamTrailMs ?? DEFAULT_VFX.vfxBeamTrailMs,
    vfxTrailMs: prof.vfxTrailMs ?? DEFAULT_VFX.vfxTrailMs,
    vfxSmokeTrailMs: prof.vfxSmokeTrailMs ?? DEFAULT_VFX.vfxSmokeTrailMs,
  };
}

/** Deep-equal helper for cheap change detection in the UI. */
export function profilesEqual(a: WeaponProfile, b: WeaponProfile): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Clamp a scalar profile field to its valid range (null when not numeric). */
export function clampProfileNumber(key: keyof WeaponProfile, v: number): number | null {
  if (!Number.isFinite(v)) return null;
  const range: Partial<Record<keyof WeaponProfile, [number, number]>> = {
    sensitivity: [0.05, 50],
    zoomSensitivity: [0.01, 50],
    yawMultiplier: [0.05, 10],
    pitchMultiplier: [0.05, 10],
    fov: [60, 160],
    zoomFov: [10, 160],
    zoomTime: [0, 500],
    vfxSmokeAmount: [0, 3],
    vfxSmokeOpacity: [0, 1],
    vfxSmokeSize: [0.2, 3],
    vfxParticleAmount: [0, 3],
    vfxParticleSize: [0.2, 3],
    vfxBeamTrailMs: [0, 2000],
    vfxTrailMs: [40, 2000],
    vfxSmokeTrailMs: [40, 3000],
  };
  const r = range[key];
  if (!r) return v;
  return Math.min(r[1], Math.max(r[0], v));
}

export { isHex };
